/**
 * The link that opens a browser sign-in's window (Sign in once, S5, 2026-10-04): `/integrations?signin=<slug>`.
 *
 * The notice a person gets when a sign-in in Prest0n's browser truly ends (functions/src/prest0nSession/
 * signinLapseNotice.ts) carries it, in the email's button and on the bell row. The Integrations page reads it once its
 * list of sign-ins has loaded, and reopens that sign-in through the one resume door (connectCustomFlow.
 * resumeCustomSignIn); a slug its list does not hold leaves the page as it is. Pure; imports nothing.
 */

export const SIGNIN_LINK_PARAM = 'signin';

const SLUG_RE = /^custom-[A-Za-z0-9_-]{1,120}$/;

/** The sign-in a link names, or null (no parameter, or not a browser sign-in's slug). */
export function signinLinkSlug(search: string | null | undefined): string | null {
  if (!search) return null;
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(search).get(SIGNIN_LINK_PARAM);
  } catch {
    return null;
  }
  const slug = typeof raw === 'string' ? raw.trim() : '';
  return SLUG_RE.test(slug) ? slug : null;
}

/** The page's own address without the link's parameter (every other parameter and the hash kept). */
export function withoutSigninLink(href: string): string {
  try {
    const u = new URL(href, 'https://prest0.ai');
    u.searchParams.delete(SIGNIN_LINK_PARAM);
    const q = u.searchParams.toString();
    return `${u.pathname}${q ? `?${q}` : ''}${u.hash}`;
  } catch {
    return '/integrations';
  }
}

/** The path a bell row for a sign-in links to, or null when the slug is not a browser sign-in's. */
export function signinLinkPath(slug: unknown): string | null {
  const s = typeof slug === 'string' ? slug.trim() : '';
  return SLUG_RE.test(s) ? `/integrations?${SIGNIN_LINK_PARAM}=${encodeURIComponent(s)}` : null;
}

/**
 * The link that brings a connected app to the front (2026-10-17): `/integrations?connect=<toolkit>`, carried by the
 * notice that a routine waits on an app to be reconnected (and its waiting chip). The page filters to that app and
 * leaves the one tap that connects it: the connect window has to open on the person's own press.
 */
export const CONNECT_LINK_PARAM = 'connect';

const TOOLKIT_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/** The app a connect link names, or null (no parameter, or not a toolkit's slug). */
export function connectLinkToolkit(search: string | null | undefined): string | null {
  if (!search) return null;
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(search).get(CONNECT_LINK_PARAM);
  } catch {
    return null;
  }
  const slug = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return TOOLKIT_RE.test(slug) ? slug : null;
}

/** The page's own address without the connect link's parameter (every other parameter and the hash kept). */
export function withoutConnectLink(href: string): string {
  try {
    const u = new URL(href, 'https://prest0.ai');
    u.searchParams.delete(CONNECT_LINK_PARAM);
    const q = u.searchParams.toString();
    return `${u.pathname}${q ? `?${q}` : ''}${u.hash}`;
  } catch {
    return '/integrations';
  }
}
